/**
 * Per-tenant managed-AI panel (OCP-401 / OCP-500, epic OCP-395).
 *
 * orboto AI is one PAYG subscription per tenant. Shows whether this tenant is
 * wired to the managed AI gateway (LiteLLM) + the non-secret model /
 * dimensions / base URL, and offers a "Rotate key" action that re-mints the
 * tenant's virtual key + redeploys.
 * The virtual key material is NEVER shown - only its wired status + when it
 * was last issued.
 *
 * Fed from the parent's `/v1/admin/tenants/:id` payload (`managed_ai`) rather
 * than fetching its own state, so it renders inline without a second
 * round-trip; the rotate action calls back `onChanged` to refresh the page.
 */
import { useCallback, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime, fmtDate } from '../lib/format-time';

export interface ManagedAiUsage {
  /** OCP-584 - lifetime per-model aggregate (may be absent pre-deploy). */
  models?: Array<{ model: string; total_tokens: number; amount_microcents: number }>;
  total_tokens: number;
  total_microcents_debited: number;
  owed_microcents: number;
  last_sweep_at: string | null;
  last_window_end: string | null;
  gated: boolean;
  blocked_at: string | null;
  /** OCP-573 - why the gate closed (closed enum); null/absent = unknown. */
  blocked_reason?: string | null;
}

/** OCP-573 - operator-facing labels for the gate-close reasons. */
const GATE_REASON_LABELS: Record<string, string> = {
  allowance_exhausted: 'included allowance exhausted (overage off)',
  overage_cap_reached: 'monthly overage cap reached',
  wallet_empty: 'wallet empty',
};

/** OCP-572 - account-level included-allowance state (grant is live). */
export interface ManagedAiAllowance {
  month: string;
  grant_microcents: number;
  consumed_microcents: number;
  remaining_microcents: number;
  overage_this_month_microcents: number;
  entitlements: Array<{ tier: string; seats: number; source: 'subscription' | 'code' }>;
}

/** OCP-585 - the gateway backstop as last synced, with the funding it mirrors. */
/** OCP-640 - the demo key's fixed backstop (daily at the gateway, monthly by OCP). */
export interface ManagedAiDemoBudget {
  daily_budget_usd: number;
  monthly_budget_usd: number;
  calls_per_window: number;
  month: string;
  month_spend_usd: number;
  today_spend_usd: number;
  budget_reset_at: string | null;
  daily_tripped_at: string | null;
  monthly_blocked_at: string | null;
}

export interface ManagedAiGatewayBudget {
  /** OCP-640 - set on the demo tenant; the wallet formula below does not apply then. */
  demo?: ManagedAiDemoBudget | null;
  max_budget_usd: number;
  accrued_spend_usd: number;
  fundable_microcents: number;
  allowance_remaining_microcents: number;
  overage_fundable_microcents: number;
  wallet_balance_microcents: number;
  overage_mode: string | null;
  monthly_overage_cap_eur_cents: number | null;
  month: string | null;
  resolution: 'resolved' | 'fallback';
  status: string;
  error: string | null;
  trigger: string;
  synced_at: string;
}

export interface ManagedAiInfo {
  gateway_configured: boolean;
  wired: boolean;
  model: string;
  dimensions: number | null;
  /** OCP-626 - the chat model core seeds (pinned | fleet default | none) + the reachable list. */
  chat_model?: string;
  chat_model_source?: 'pinned' | 'fleet' | 'none';
  fleet_chat_model?: string;
  chat_models?: string[];
  /**
   * OCP-641 - the effective AI reviewer this tenant's key sees in the
   * catalogue: a pinned model_name, the literal `off`, the fleet default
   * (ai.recommended_reviewer_model) or '' when none.
   */
  reviewer_model?: string;
  reviewer_model_source?: 'pinned' | 'off' | 'fleet' | 'none';
  fleet_reviewer_model?: string;
  base_url: string;
  key_rotated_at: string | null;
  /** OCP-400 - customer price, microcents per 1M tokens. Null when unset. */
  price_microcents_per_mtok?: number | null;
  /** OCP-400 - per-tenant metered usage + spend + gate. Null when unavailable. */
  usage?: ManagedAiUsage | null;
  /** OCP-572 - the ACCOUNT allowance this tenant draws from first. */
  allowance?: ManagedAiAllowance | null;
  /** OCP-585 - the gateway max_budget backstop as last synced; null until the first sync. */
  gateway_budget?: ManagedAiGatewayBudget | null;
  /**
   * OCP-634 / core ORB-2002 - the quality guards that fired per operation in
   * the last 24 h of beacons, summed per (operation, guard), loudest first.
   * Empty = older core or nothing fired.
   */
  guards_24h?: ManagedAiGuardCount[];
}

export interface ManagedAiGuardCount {
  operation: string;
  guard: string;
  count: number;
}

/** Microcents (1 EUR cent = 10,000) -> "€0.69" for display. */
function eur(microcents: number): string {
  return `€${(microcents / 1_000_000).toFixed(2)}`;
}

/** Compact token count, e.g. 2_300_000 -> "2.3M". */
function tokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

export function ManagedAiPanel({
  tenantId,
  info,
  provisioned,
  onChanged,
}: {
  tenantId: string;
  info: ManagedAiInfo;
  provisioned: boolean;
  onChanged: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // OCP-626 - chat-model pin editor: '' = follow the fleet default.
  const [chatPin, setChatPin] = useState<string>(info.chat_model_source === 'pinned' ? (info.chat_model ?? '') : '');
  const [chatBusy, setChatBusy] = useState(false);
  const chatDirty = chatPin !== (info.chat_model_source === 'pinned' ? (info.chat_model ?? '') : '');

  const saveChatModel = useCallback(async () => {
    setChatBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api(`/v1/admin/tenants/${tenantId}/chat-model`, { method: 'PATCH', body: { chat_model: chatPin || null } });
      setNotice(chatPin ? `Chat model pinned to ${chatPin} - the tenant is redeploying to apply it.` : 'Chat model follows the fleet default again - the tenant is redeploying.');
      onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; message?: string } | null;
        setError(body?.message ?? `Could not change the chat model (${body?.error ?? err.status}).`);
      } else {
        setError('Could not change the chat model.');
      }
    } finally {
      setChatBusy(false);
    }
  }, [tenantId, chatPin, onChanged]);

  // OCP-641 - reviewer override editor: '' = fleet default (clear), 'off' =
  // reviewer disabled for this tenant, a model_name = pin.
  const reviewerCurrent =
    info.reviewer_model_source === 'pinned' || info.reviewer_model_source === 'off' ? (info.reviewer_model ?? '') : '';
  const [reviewerSel, setReviewerSel] = useState<string>(reviewerCurrent);
  const [reviewerBusy, setReviewerBusy] = useState(false);
  const reviewerDirty = reviewerSel !== reviewerCurrent;

  const saveReviewerModel = useCallback(async () => {
    setReviewerBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api<{ reviewer_model: string; reviewer_model_source: string; effective_within_seconds: number }>(
        `/v1/admin/tenants/${tenantId}/reviewer-model`,
        { method: 'PATCH', body: { reviewer_model: reviewerSel || null } },
      );
      const within = `effective within ${res.effective_within_seconds ?? 60} s, no redeploy`;
      setNotice(
        reviewerSel === 'off'
          ? `Reviewer disabled for this tenant - ${within}.`
          : reviewerSel
            ? `Reviewer pinned to ${reviewerSel} - ${within}.`
            : `Reviewer follows the fleet default again (${res.reviewer_model || 'none'}) - ${within}.`,
      );
      onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; message?: string } | null;
        setError(body?.message ?? `Could not change the reviewer (${body?.error ?? err.status}).`);
      } else {
        setError('Could not change the reviewer.');
      }
    } finally {
      setReviewerBusy(false);
    }
  }, [tenantId, reviewerSel, onChanged]);

  const rotate = useCallback(async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api(`/v1/admin/tenants/${tenantId}/rotate-ai-key`, { method: 'POST' });
      setConfirm(false);
      setNotice('New AI key minted - the tenant is redeploying to apply it.');
      onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        if (body?.error === 'gateway_unconfigured') {
          setError('The managed AI gateway is not configured. Set AI_GATEWAY_* first.');
        } else if (body?.error === 'not_provisioned') {
          setError('This tenant has no Coolify service yet - provision it first.');
        } else {
          setError(`Could not rotate the AI key (HTTP ${err.status}).`);
        }
      } else {
        setError('Could not rotate the AI key.');
      }
    } finally {
      setBusy(false);
    }
  }, [tenantId, onChanged]);

  return (
    <section className="mt-10 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Managed AI</h2>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        Every tenant is wired to the managed AI gateway at provision (one pay-as-you-go
        subscription over the wallet; embeddings today, chat next over the same key). Rotating
        re-mints this tenant&apos;s virtual key and redeploys to apply it.
      </p>

      {/* OCP-626 - per-tenant chat-model pin. The list is what the tenant key
          can reach (ai.chat_models); the fleet default is ai.recommended_chat_model. */}
      {provisioned && info.gateway_configured && (
        <div className="mt-4 flex flex-wrap items-end gap-2 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3">
          <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-400">
            Chat model for this tenant
            <select
              value={chatPin}
              onChange={(e) => setChatPin(e.target.value)}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
              data-testid="managed-ai-chat-pin"
            >
              <option value="">Fleet default ({info.fleet_chat_model || 'none'})</option>
              {(info.chat_models ?? []).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void saveChatModel()}
            disabled={!chatDirty || chatBusy}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
            data-testid="managed-ai-chat-save"
          >
            {chatBusy ? 'Applying…' : 'Apply and redeploy'}
          </button>
          <span className="text-xs text-gray-500">
            Pinning holds this tenant on one model while the fleet default moves; the change redeploys the tenant.
          </span>
        </div>
      )}

      {/* OCP-641 - per-tenant reviewer override. Applied catalogue-side for
          this tenant's key (recommended_default.reviewer): no env var, no
          redeploy, and the demo's 30-minute workspace-settings reset cannot
          undo it. "off" is the operator decision for the public demo. */}
      {provisioned && info.gateway_configured && (
        <div className="mt-2 flex flex-wrap items-end gap-2 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3">
          <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-400">
            Reviewer for this tenant
            <select
              value={reviewerSel}
              onChange={(e) => setReviewerSel(e.target.value)}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
              data-testid="managed-ai-reviewer-select"
            >
              <option value="">Fleet default ({info.fleet_reviewer_model || 'none'})</option>
              <option value="off">off - no reviewer pass for this tenant</option>
              {(info.chat_models ?? []).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void saveReviewerModel()}
            disabled={!reviewerDirty || reviewerBusy}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
            data-testid="managed-ai-reviewer-save"
          >
            {reviewerBusy ? 'Saving…' : 'Apply (no redeploy)'}
          </button>
          <span className="text-xs text-gray-500">
            Catalogue-side for this tenant&apos;s key: effective within 60 s, survives the demo reset. The reviewer must differ from the chat model.
          </span>
        </div>
      )}

      {!info.gateway_configured && (
        <div className="mt-3 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          Gateway not configured - managed AI is off fleet-wide. Set{' '}
          <span className="font-mono">AI_GATEWAY_BASE_URL</span> +{' '}
          <span className="font-mono">AI_GATEWAY_MASTER_KEY</span> in the api env.
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StateField label="Wired">
          {info.wired ? (
            <span className="inline-flex items-center rounded bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-300 ring-1 ring-inset ring-green-200 dark:ring-green-800">
              key wired
            </span>
          ) : (
            <span className="inline-flex items-center rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400 ring-1 ring-inset ring-gray-200 dark:ring-gray-700">
              not wired
            </span>
          )}
        </StateField>
        <StateField label="Model">
          <span className="font-mono text-sm">{info.model || '-'}</span>
        </StateField>
        <StateField label="Dimensions">
          <span className="font-mono text-sm">{info.dimensions ?? '-'}</span>
        </StateField>
        <StateField label="Chat model">
          <span className="font-mono text-sm" data-testid="managed-ai-chat-model">{info.chat_model || '-'}</span>{' '}
          <span className="text-xs text-gray-500" data-testid="managed-ai-chat-model-source">
            {info.chat_model_source === 'pinned'
              ? `pinned (fleet default ${info.fleet_chat_model || '-'})`
              : info.chat_model_source === 'fleet'
                ? 'fleet default'
                : 'none - no chat seeded'}
          </span>
        </StateField>
        <StateField label="Reviewer">
          <span className="font-mono text-sm" data-testid="managed-ai-reviewer-model">{info.reviewer_model || '-'}</span>{' '}
          <span className="text-xs text-gray-500" data-testid="managed-ai-reviewer-model-source">
            {info.reviewer_model_source === 'pinned'
              ? `pinned (fleet default ${info.fleet_reviewer_model || '-'})`
              : info.reviewer_model_source === 'off'
                ? `off for this tenant (fleet default ${info.fleet_reviewer_model || '-'})`
                : info.reviewer_model_source === 'fleet'
                  ? 'fleet default'
                  : 'none - core skips the reviewer'}
          </span>
        </StateField>
        <StateField label="Gateway">
          <span className="font-mono text-sm break-all text-gray-500">{info.base_url || '-'}</span>
        </StateField>
        <StateField label="Key issued">
          <span className="text-sm">
            {info.key_rotated_at ? fmtDate(info.key_rotated_at) : '-'}
          </span>
        </StateField>
      </div>

      {/* OCP-634 - guard telemetry: which quality guards fired in the last 24 h. */}
      <div className="mt-4" data-testid="managed-ai-guards">
        <StateField label="Guards (24 h)">
          {info.guards_24h && info.guards_24h.length > 0 ? (
            <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {info.guards_24h.map((g) => (
                <li key={`${g.operation} ${g.guard}`} className="font-mono">
                  {g.operation}: {g.guard} x {g.count}
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">none reported</span>
          )}
        </StateField>
      </div>

      {/* OCP-400 - metered usage + spend + balance-gate status. */}
      {info.usage && (
        <div className="mt-6" data-testid="managed-ai-usage">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              Usage &amp; spend
            </h3>
            {info.usage.gated ? (
              <span
                data-testid="managed-ai-gated"
                className="inline-flex items-center rounded bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300 ring-1 ring-inset ring-red-200 dark:ring-red-800"
                title={
                  info.usage.blocked_at
                    ? `Key blocked ${fmtDateTime(info.usage.blocked_at)}`
                    : undefined
                }
              >
                key blocked
                {info.usage.blocked_reason
                  ? ` - ${GATE_REASON_LABELS[info.usage.blocked_reason] ?? info.usage.blocked_reason}`
                  : ' - spend boundary hit'}
              </span>
            ) : (
              <span className="inline-flex items-center rounded bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-300 ring-1 ring-inset ring-green-200 dark:ring-green-800">
                active
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            AI usage (embeddings today) is metered every 15 min: the account&apos;s included
            per-seat allowance is consumed first (OCP-572), then - only with customer-activated
            overage - the wallet.
            {typeof info.price_microcents_per_mtok === 'number' && (
              <>
                {' '}
                Price: {eur(info.price_microcents_per_mtok)} / 1M tokens.
              </>
            )}
          </p>
          {(info.usage.models?.length ?? 0) > 0 && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-testid="managed-ai-models">
              By model:{' '}
              {info.usage.models!
                .map((m) => `${m.model} ${tokens(m.total_tokens)} (${eur(m.amount_microcents)})`)
                .join(' · ')}
            </p>
          )}
          {info.allowance && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-testid="managed-ai-allowance">
              Included allowance ({info.allowance.month}):{' '}
              <span className="font-mono">{eur(info.allowance.consumed_microcents)}</span> of{' '}
              <span className="font-mono">{eur(info.allowance.grant_microcents)}</span> used
              {info.allowance.entitlements.length > 0 && (
                <>
                  {' '}
                  ({info.allowance.entitlements
                    .map((e) => `${e.tier} × ${e.seats} seat${e.seats === 1 ? '' : 's'}`)
                    .join(' + ')})
                </>
              )}
              {info.allowance.overage_this_month_microcents > 0 && (
                <>
                  {' '}
                  · wallet overage this month:{' '}
                  <span className="font-mono">
                    {eur(info.allowance.overage_this_month_microcents)}
                  </span>
                </>
              )}
            </p>
          )}
          {info.gateway_budget?.demo && (
            <p
              className={`mt-1 text-xs ${info.gateway_budget.demo.monthly_blocked_at ? 'text-red-700 dark:text-red-300' : 'text-gray-600 dark:text-gray-400'}`}
              data-testid="managed-ai-demo-budget"
            >
              Demo spend backstop (OCP-640): {info.gateway_budget.demo.calls_per_window} calls per reset window (core), today{' '}
              <span className="font-mono">${info.gateway_budget.demo.today_spend_usd.toFixed(2)}</span> of{' '}
              <span className="font-mono">${info.gateway_budget.demo.daily_budget_usd.toFixed(2)}</span>/day at the gateway
              {info.gateway_budget.demo.budget_reset_at ? ` (resets ${fmtDateTime(info.gateway_budget.demo.budget_reset_at)})` : ''}, month{' '}
              <span className="font-mono">${info.gateway_budget.demo.month_spend_usd.toFixed(2)}</span> of{' '}
              <span className="font-mono">${info.gateway_budget.demo.monthly_budget_usd.toFixed(2)}</span>
              {info.gateway_budget.demo.daily_tripped_at && ` · daily cap tripped ${fmtDateTime(info.gateway_budget.demo.daily_tripped_at)}`}
              {info.gateway_budget.demo.monthly_blocked_at && ` · KEY BLOCKED since ${fmtDateTime(info.gateway_budget.demo.monthly_blocked_at)} (monthly cap), unblocks at the month rollover`}
              , synced {fmtDateTime(info.gateway_budget.synced_at)}
              {info.gateway_budget.status === 'failed' && ` · push FAILED: ${info.gateway_budget.error ?? 'unknown'}`}
            </p>
          )}
          {info.gateway_budget && !info.gateway_budget.demo && (
            <p
              className={`mt-1 text-xs ${
                info.gateway_budget.resolution === 'fallback' || info.gateway_budget.status === 'failed'
                  ? 'text-amber-700 dark:text-amber-300'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
              data-testid="managed-ai-gateway-budget"
            >
              Gateway backstop: max_budget{' '}
              <span className="font-mono">${info.gateway_budget.max_budget_usd.toFixed(2)}</span> = accrued{' '}
              <span className="font-mono">${info.gateway_budget.accrued_spend_usd.toFixed(2)}</span> + fundable{' '}
              <span className="font-mono">{eur(info.gateway_budget.fundable_microcents)}</span> (allowance{' '}
              {eur(info.gateway_budget.allowance_remaining_microcents)} + wallet overage{' '}
              {eur(info.gateway_budget.overage_fundable_microcents)}
              {info.gateway_budget.overage_mode === 'use_credits'
                ? `, use_credits cap €${((info.gateway_budget.monthly_overage_cap_eur_cents ?? 0) / 100).toFixed(2)}`
                : ', hard_gate: wallet not fundable'}
              ), synced {fmtDateTime(info.gateway_budget.synced_at)} via{' '}
              {info.gateway_budget.trigger}
              {info.gateway_budget.resolution === 'fallback' && ' · FALLBACK: allowance resolution failed, wallet-only formula, never lowered'}
              {info.gateway_budget.status === 'failed' && ` · push FAILED: ${info.gateway_budget.error ?? 'unknown'}`}
            </p>
          )}
          {!info.gateway_budget && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-testid="managed-ai-gateway-budget-none">
              Gateway backstop: not synced yet (next sweep).
            </p>
          )}
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StateField label="Tokens metered">
              <span className="font-mono text-sm" data-testid="managed-ai-tokens">
                {tokens(info.usage.total_tokens)}
              </span>
            </StateField>
            <StateField label="Debited">
              <span className="font-mono text-sm" data-testid="managed-ai-debited">
                {eur(info.usage.total_microcents_debited)}
              </span>
            </StateField>
            <StateField label="Owed (unpaid)">
              <span
                className={`font-mono text-sm ${
                  info.usage.owed_microcents > 0 ? 'text-red-600 dark:text-red-400' : ''
                }`}
                data-testid="managed-ai-owed"
              >
                {eur(info.usage.owed_microcents)}
              </span>
            </StateField>
            <StateField label="Last sweep">
              <span className="text-sm">
                {info.usage.last_sweep_at
                  ? fmtDateTime(info.usage.last_sweep_at)
                  : 'never'}
              </span>
            </StateField>
          </div>
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {error}
        </div>
      )}
      {notice && (
        <div className="mt-4 text-sm font-medium text-green-700 dark:text-green-400">{notice}</div>
      )}

      {!provisioned ? (
        <p className="mt-4 text-sm text-gray-500">
          No Coolify service yet - rotating needs a provisioned tenant.
        </p>
      ) : (
        <div className="mt-5">
          {confirm ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Re-mint the key and redeploy?
              </span>
              <button
                type="button"
                onClick={() => void rotate()}
                disabled={busy}
                className="rounded bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? 'Rotating…' : 'Confirm rotate'}
              </button>
              <button
                type="button"
                onClick={() => setConfirm(false)}
                disabled={busy}
                className="rounded border border-gray-300 dark:border-gray-700 px-4 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirm(true)}
              disabled={!info.gateway_configured}
              className="rounded bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
              title={info.gateway_configured ? undefined : 'Gateway not configured'}
            >
              {info.wired ? 'Rotate key' : 'Wire managed AI'}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function StateField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
